import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, Upload, Plus, KeyRound, UserPlus, Download, FileText, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail, API } from "@/lib/api";

function formatSize(b) {
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
}

export default function AdminClientPortal() {
  const { user, ready } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAcc, setShowNewAcc] = useState(false);
  const [newAcc, setNewAcc] = useState({ name: "", email: "", password: "", notes: "" });
  const [uploadFor, setUploadFor] = useState(null); // account id
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [a, d] = await Promise.all([
        api.get("/admin/client-accounts"),
        api.get("/admin/documents"),
      ]);
      setAccounts(a.data);
      setDocs(d.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ready && user) fetchAll();
  }, [ready, user]);

  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  const createAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/client-accounts", newAcc);
      toast.success(`Compte créé pour ${newAcc.email}`);
      setNewAcc({ name: "", email: "", password: "", notes: "" });
      setShowNewAcc(false);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    }
  };

  const resetPwd = async (id, email) => {
    const pwd = window.prompt(`Nouveau mot de passe pour ${email} (min 6 caractères) :`);
    if (!pwd) return;
    try {
      await api.patch(`/admin/client-accounts/${id}/password`, { password: pwd });
      toast.success("Mot de passe mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const deleteAccount = async (id, email) => {
    if (!window.confirm(`Supprimer le compte ${email} et TOUS ses documents ?`)) return;
    try {
      await api.delete(`/admin/client-accounts/${id}`);
      toast.success("Compte supprimé");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadFor) return;
    if (!uploadLabel.trim()) {
      toast.error("Indiquez un libellé pour le document.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("owner_id", uploadFor);
    fd.append("label", uploadLabel.trim());
    fd.append("category", uploadCategory.trim());
    fd.append("file", file);
    try {
      await api.post("/admin/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document ajouté");
      setUploadFor(null);
      setUploadLabel("");
      setUploadCategory("");
      if (fileRef.current) fileRef.current.value = "";
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const downloadDoc = async (id, filename) => {
    try {
      const token = localStorage.getItem("eq_token");
      const res = await fetch(`${API}/admin/documents/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      toast.success("Document supprimé");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const docsForAcc = (id) => docs.filter((d) => d.owner_id === id);

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-admin-portal">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-6" data-testid="portal-back-admin">
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Espace client</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
              Comptes & documents
            </h1>
            <p className="mt-2 text-zinc-400 text-sm">
              Créez des comptes clients et téléversez les documents qui leurs sont destinés.
            </p>
          </div>
          <button
            onClick={() => setShowNewAcc(true)}
            data-testid="new-account-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-black font-semibold"
          >
            <UserPlus size={16} /> Nouveau compte client
          </button>
        </div>

        {/* New account form */}
        {showNewAcc && (
          <form onSubmit={createAccount} className="mt-6 rounded-xl border border-white/10 bg-[#0e0e0e] p-6" data-testid="new-account-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                type="text" required placeholder="Nom complet du client" value={newAcc.name}
                onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })}
                className="bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white text-sm"
                data-testid="new-account-name"
              />
              <input
                type="email" required placeholder="Email" value={newAcc.email}
                onChange={(e) => setNewAcc({ ...newAcc, email: e.target.value })}
                className="bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white text-sm"
                data-testid="new-account-email"
              />
              <input
                type="text" required minLength={6} placeholder="Mot de passe initial (min 6)" value={newAcc.password}
                onChange={(e) => setNewAcc({ ...newAcc, password: e.target.value })}
                className="bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white text-sm"
                data-testid="new-account-password"
              />
              <input
                type="text" placeholder="Notes internes (optionnel)" value={newAcc.notes}
                onChange={(e) => setNewAcc({ ...newAcc, notes: e.target.value })}
                className="bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white text-sm"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button type="submit" className="px-5 py-2 rounded-md bg-white text-black font-semibold" data-testid="new-account-submit">
                Créer
              </button>
              <button type="button" onClick={() => setShowNewAcc(false)} className="px-5 py-2 rounded-md border border-white/15">
                Annuler
              </button>
            </div>
          </form>
        )}

        {/* Accounts list */}
        <div className="mt-10" data-testid="accounts-list">
          {loading ? (
            <div className="p-12 text-center text-zinc-400">
              <Loader2 className="inline animate-spin mr-2" /> Chargement…
            </div>
          ) : accounts.length === 0 ? (
            <div className="rounded-xl border border-white/10 p-12 text-center text-zinc-500">
              Aucun compte client. Cliquez sur "Nouveau compte client" pour commencer.
            </div>
          ) : (
            <div className="space-y-4">
              {accounts.map((a) => (
                <div key={a.id} className="rounded-xl border border-white/10 bg-[#0e0e0e] p-6" data-testid={`account-${a.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                      <div className="font-display text-xl font-semibold">{a.name}</div>
                      <div className="text-sm text-zinc-400">{a.email}</div>
                      {a.notes && <div className="text-xs text-zinc-500 mt-1">{a.notes}</div>}
                      <div className="text-[10px] text-zinc-600 mt-2 uppercase tracking-wider">
                        Créé le {new Date(a.created_at).toLocaleDateString("fr-FR")}
                        {a.last_login && ` · Dernière connexion : ${new Date(a.last_login).toLocaleDateString("fr-FR")}`}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => { setUploadFor(a.id); setUploadLabel(""); setUploadCategory(""); }}
                        data-testid={`upload-for-${a.id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white text-black text-sm font-semibold"
                      >
                        <Plus size={14} /> Ajouter un document
                      </button>
                      <button
                        onClick={() => resetPwd(a.id, a.email)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/15 text-sm"
                      >
                        <KeyRound size={14} /> Mot de passe
                      </button>
                      <button
                        onClick={() => deleteAccount(a.id, a.email)}
                        className="p-2 rounded-md border border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Upload form for this account */}
                  {uploadFor === a.id && (
                    <div className="mt-5 pt-5 border-t border-white/5 grid sm:grid-cols-3 gap-3" data-testid="upload-form">
                      <input
                        type="text" placeholder="Libellé du document *" value={uploadLabel}
                        onChange={(e) => setUploadLabel(e.target.value)}
                        className="bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-white text-sm"
                        data-testid="upload-label"
                      />
                      <input
                        type="text" placeholder="Catégorie (devis, facture…)" value={uploadCategory}
                        onChange={(e) => setUploadCategory(e.target.value)}
                        className="bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-white text-sm"
                      />
                      <div className="flex gap-2">
                        <input
                          ref={fileRef}
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={onFile}
                          disabled={uploading}
                          className="hidden"
                          data-testid="upload-file"
                        />
                        <button
                          type="button"
                          onClick={() => fileRef.current?.click()}
                          disabled={uploading || !uploadLabel.trim()}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-black text-sm font-semibold disabled:opacity-50 flex-1 justify-center"
                          data-testid="upload-trigger"
                        >
                          {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                          Sélectionner
                        </button>
                        <button
                          type="button"
                          onClick={() => setUploadFor(null)}
                          className="px-3 py-2 rounded-md border border-white/15 text-sm"
                        >
                          Annuler
                        </button>
                      </div>
                      <div className="sm:col-span-3 text-xs text-zinc-500">
                        Formats acceptés : PDF, PNG, JPEG, WebP, GIF — max 50 Mo.
                      </div>
                    </div>
                  )}

                  {/* Documents of this account */}
                  {docsForAcc(a.id).length > 0 && (
                    <ul className="mt-5 space-y-2" data-testid={`docs-${a.id}`}>
                      {docsForAcc(a.id).map((d) => (
                        <li
                          key={d.id}
                          className="flex items-center gap-3 px-3 py-2 rounded-md bg-[#141414] border border-white/5"
                          data-testid={`doc-${d.id}`}
                        >
                          <div className="text-zinc-400">
                            {d.content_type?.startsWith("image/") ? <ImageIcon size={16} /> : <FileText size={16} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{d.label}</div>
                            <div className="text-xs text-zinc-500 truncate">
                              {d.filename} · {formatSize(d.size)}
                              {d.category && ` · ${d.category}`}
                              {" · "}{new Date(d.created_at).toLocaleDateString("fr-FR")}
                            </div>
                          </div>
                          <button
                            onClick={() => downloadDoc(d.id, d.filename)}
                            className="p-2 text-zinc-400 hover:text-white"
                            data-testid={`doc-download-${d.id}`}
                          >
                            <Download size={14} />
                          </button>
                          <button
                            onClick={() => deleteDoc(d.id)}
                            className="p-2 text-zinc-400 hover:text-rose-400"
                            data-testid={`doc-delete-${d.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
